import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import "./LogoLoop.css";

export type LogoItem =
  | { node: ReactNode; title?: string; href?: string; ariaLabel?: string }
  | { src: string; alt?: string; title?: string; href?: string; srcSet?: string; sizes?: string; width?: number; height?: number };

export interface LogoLoopProps {
  logos: LogoItem[];
  speed?: number;
  direction?: "left" | "right";
  width?: number | string;
  logoHeight?: number;
  gap?: number;
  pauseOnHover?: boolean;
  fadeOut?: boolean;
  fadeOutColor?: string;
  scaleOnHover?: boolean;
  ariaLabel?: string;
  className?: string;
  style?: CSSProperties;
}

export default function LogoLoop({
  logos,
  speed = 80,
  direction = "left",
  width = "100%",
  logoHeight = 36,
  gap = 48,
  pauseOnHover = true,
  fadeOut = true,
  fadeOutColor,
  scaleOnHover = true,
  ariaLabel = "Partner logos",
  className,
  style,
}: LogoLoopProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const seqRef = useRef<HTMLUListElement>(null);
  const [copies, setCopies] = useState(2);
  const [seqWidth, setSeqWidth] = useState(0);

  useEffect(() => {
    if (!seqRef.current || !trackRef.current) return;
    const measure = () => {
      const sw = seqRef.current!.getBoundingClientRect().width;
      const tw = trackRef.current!.parentElement!.getBoundingClientRect().width;
      if (sw > 0) {
        setSeqWidth(sw);
        setCopies(Math.max(2, Math.ceil((tw * 2) / sw) + 1));
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(seqRef.current);
    ro.observe(trackRef.current.parentElement!);
    return () => ro.disconnect();
  }, [logos, gap, logoHeight]);

  const duration = seqWidth > 0 ? seqWidth / speed : 30;

  const cssVars = useMemo(
    () =>
      ({
        "--logoloop-gap": `${gap}px`,
        "--logoloop-logoHeight": `${logoHeight}px`,
        "--logoloop-duration": `${duration}s`,
        "--logoloop-direction": direction === "left" ? "normal" : "reverse",
        ...(fadeOutColor ? { "--logoloop-fadeColor": fadeOutColor } : {}),
      }) as CSSProperties,
    [gap, logoHeight, duration, direction, fadeOutColor],
  );

  const renderItem = (item: LogoItem, idx: number, hidden: boolean) => {
    const isNode = "node" in item;
    const content = isNode ? (
      <span className="logoloop__node" aria-hidden="true">
        {item.node}
      </span>
    ) : (
      <img src={item.src} alt={item.alt ?? item.title ?? ""} loading="lazy" draggable={false} />
    );
    const inner = item.href ? (
      <a
        href={item.href}
        target="_blank"
        rel="noreferrer noopener"
        aria-label={isNode ? item.ariaLabel ?? item.title : item.alt ?? item.title}
        tabIndex={hidden ? -1 : 0}
      >
        {content}
      </a>
    ) : (
      <>
        {content}
        {isNode && (item.ariaLabel ?? item.title) ? (
          <span className="sr-only">{item.ariaLabel ?? item.title}</span>
        ) : null}
      </>
    );
    return (
      <li
        key={idx}
        className="logoloop__item"
        title={item.title}
      >
        {inner}
      </li>
    );
  };

  return (
    <div
      className={`logoloop ${pauseOnHover ? "logoloop--pause-on-hover" : ""} ${fadeOut ? "logoloop--fade" : ""} ${scaleOnHover ? "logoloop--scale" : ""} ${className ?? ""}`}
      style={{ ...cssVars, width, ...style }}
      role="region"
      aria-label={ariaLabel}
    >
      <div className="logoloop__track" ref={trackRef}>
        {Array.from({ length: copies }).map((_, copyIdx) => (
          <ul
            key={copyIdx}
            ref={copyIdx === 0 ? seqRef : undefined}
            className="logoloop__list"
            role="list"
            aria-hidden={copyIdx > 0}
          >
            {logos.map((item, i) => renderItem(item, i, copyIdx > 0))}
          </ul>
        ))}
      </div>
    </div>
  );
}
